import React, { useEffect, useState } from 'react';
import { MinimizeIcon } from 'lucide-react';
import { RentalIncomePanel } from '../components/RentalIncomePanel';
import { HousingPaymentPanel } from '../components/HousingPaymentPanel';
import { DscrResultBar } from '../components/DscrResultBar';
import { ScenarioHistoryPanel } from '../components/ScenarioHistoryPanel';
import { SaveScenarioModal } from '../components/SaveScenarioModal';
import { useDscrCalculator } from '../hooks/useDscrCalculator';
import { currentUser } from '../data/scenarios';
import { loan } from '../data/loan';
interface DscrCalculatorProps {
  showScenarioHistory?: boolean;
}
export function DscrCalculator({
  showScenarioHistory = true
}: DscrCalculatorProps) {
  const calculator = useDscrCalculator();
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const nextScenarioNumber = calculator.scenarios.length + 1;
  useEffect(() => {
    if (!isPoppedOut) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaveOpen) setIsPoppedOut(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isPoppedOut, isSaveOpen]);
  const body = <div className={`mx-auto flex flex-col gap-4 ${isPoppedOut ? 'max-w-none' : 'max-w-[1400px]'}`} data-id="element-122" data-mp="3167reriqvzaz">
      {confirmation ? <p role="status" className="rounded border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" data-id="element-123" data-mp="361wuv5xvy0ck">
          {confirmation}
        </p> : null}

      <DscrResultBar units={calculator.units} payment={calculator.housingPayment} dscr={calculator.dscr} isDirty={calculator.isDirty} onSave={() => setIsSaveOpen(true)} onPopOut={isPoppedOut ? undefined : () => setIsPoppedOut(true)} data-id="element-124" data-mp="3fvwzl6p993kr" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-id="element-125" data-mp="3tr7l3thm0ccg">
        <RentalIncomePanel units={calculator.units} onChangeRent={calculator.setUnitRent} onChangeLabel={calculator.setUnitLabel} onAddUnit={calculator.addUnit} onRemoveUnit={calculator.removeUnit} data-id="element-126" data-mp="1trubs1xwpcln" />
        <HousingPaymentPanel payment={calculator.housingPayment} onChangeField={calculator.setPaymentField} onRefresh={calculator.refreshFromArrive} isRefreshing={calculator.isRefreshing} isOverridden={calculator.housingPaymentOverridden} data-id="element-127" data-mp="1yqfec80ms8d9" />
      </div>

      {showScenarioHistory ? <ScenarioHistoryPanel scenarios={calculator.scenarios} activeScenarioId={calculator.activeScenarioId} onLoad={scenario => {
      calculator.loadScenario(scenario);
      setConfirmation(`Loaded “${scenario.name}”.`);
    }} data-id="element-128" data-mp="2mef5h91uu0sd" /> : null}
    </div>;
  const saveModal = <SaveScenarioModal open={isSaveOpen} defaultName={`Scenario ${nextScenarioNumber}`} dscr={calculator.dscr} savedBy={currentUser} scenarios={calculator.scenarios} activeScenarioId={calculator.activeScenarioId} onClose={() => setIsSaveOpen(false)} onSave={(name, note) => {
    const scenario = calculator.saveScenario(name, note);
    setIsSaveOpen(false);
    setConfirmation(`Saved “${scenario.name}” as ${currentUser}.`);
  }} onOverwrite={(id, note) => {
    const scenario = calculator.overwriteScenario(id, note);
    setIsSaveOpen(false);
    if (scenario) {
      setConfirmation(`Overwrote “${scenario.name}” — now version ${scenario.version}, saved by ${currentUser}.`);
    }
  }} data-id="element-129" data-mp="n4yq1ogwx3s6" />;
  if (isPoppedOut) {
    return <div role="dialog" aria-modal="true" aria-label="DSCR Calculator — full screen" className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-[#f3f3f3]" data-id="element-130" data-mp="4b8hzweal80o">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-3" data-id="element-131" data-mp="25ave6bwqfmn1">
          <div className="min-w-0" data-id="element-132" data-mp="1wbhzn4tr47vg">
            <h2 className="truncate text-sm font-semibold text-slate-900" data-id="element-133" data-mp="1waoz4lldyelr">
              DSCR Calculator — Loan {loan.loanNumber}
            </h2>
            <p className="mt-1 truncate text-xs text-slate-500" data-id="element-134" data-mp="1reht6sszp33t">
              {loan.borrower} · {loan.propertyAddress}
            </p>
          </div>
          <button type="button" onClick={() => setIsPoppedOut(false)} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#0176d3]/40" data-id="element-135" data-mp="1hmc5vwdon9uy">
            <MinimizeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-136" data-mp="13m1ems8gnt99" />
            Exit full screen
          </button>
        </header>
        <main className="flex-1 px-4 py-6 md:px-8" data-id="element-137" data-mp="ok1hsr6wfl9t">{body}</main>
        {saveModal}
      </div>;
  }
  return <main className="min-h-screen w-full bg-[#f3f3f3] px-4 py-6 md:px-8" data-id="element-138" data-mp="ti31nec5fomc">
      {body}
      {saveModal}
    </main>;
}